import React, { useState, useEffect, useContext } from "react"; // Import useContext
import { ThumbsUp, ThumbsDown, Star, MessageSquareText } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Label } from "./ui/label";
import { Text } from "./catalyst/text";
import { AppContext } from "../contexts/AppContext"; // Import AppContext
import { toast } from "./ui/use-toast"; // Import toast
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";

const StarRating = ({ rating, setRating, maxStars = 5, label }) => {
  const [hoveredRating, setHoveredRating] = useState(0);

  return (
    <div className="flex items-center justify-between mb-2">
      <Label className="text-sm text-muted-foreground mr-4 flex-shrink-0 w-[160px]">
        {label}:
      </Label>
      <div className="flex flex-grow justify-start">
        {[...Array(maxStars)].map((_, index) => {
          const starValue = index + 1;
          return (
            <Button
              key={starValue}
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`${starValue} star`}
              onClick={() => setRating(starValue)}
              onMouseEnter={() => setHoveredRating(starValue)}
              onMouseLeave={() => setHoveredRating(0)}
              className="p-0 h-6 w-6 text-muted-foreground hover:text-yellow-400"
            >
              <Star
                className={`h-5 w-5 transition-colors ${
                  starValue <= (hoveredRating || rating)
                    ? "text-yellow-400 fill-yellow-400"
                    : ""
                }`}
              />
            </Button>
          );
        })}
      </div>
    </div>
  );
};

// Mobile feedback dialog component
const MobileFeedbackDialog = ({
  question = "Was this helpful?",
  showRatings = false,
  feedback = null,
  setFeedback = () => {},
  ratingLabels = [
    "Accuracy",
    "Helpfulness",
    "Coverage",
    "Efficiency",
    "Overall",
  ],
  feedbackKey,
  context = {},
  submitFeedback,
  feedbackLoading,
  isDialogOpen,
  setIsDialogOpen
}) => {
  const [internalFeedbackText, setInternalFeedbackText] = useState("");
  const [internalRatings, setInternalRatings] = useState({});
  const [selectedThumb, setSelectedThumb] = useState(null);

  // Initialize internal state when dialog opens or feedback changes
  useEffect(() => {
    const newFeedbackText = feedback?.feedbackText || "";
    const newRatings =
      feedback?.ratings ||
      ratingLabels.reduce((acc, label) => ({ ...acc, [label]: 0 }), {});

    setInternalFeedbackText(newFeedbackText);
    setInternalRatings(newRatings);
    setSelectedThumb(feedback?.feedbackFlag);
  }, [feedback, ratingLabels, isDialogOpen]);

  const handleThumbClick = (isPositive) => {
    const newThumb = selectedThumb === isPositive ? null : isPositive;
    setSelectedThumb(newThumb);
    
    // Update feedback state
    const newFeedbackData = {
      ...(feedback || {}),
      feedbackFlag: newThumb,
    };
    setFeedback(newFeedbackData);
  };

  const handleRatingChange = (label, value) => {
    const updatedRatings = { ...internalRatings, [label]: value };
    setInternalRatings(updatedRatings);

    // Update parent state
    const newFeedbackData = {
      ...(feedback || {}),
      ratings: updatedRatings,
    };
    setFeedback(newFeedbackData);
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setInternalFeedbackText(newText);

    // Update parent state
    const newFeedbackData = {
      ...(feedback || {}),
      feedbackText: newText,
    };
    setFeedback(newFeedbackData);
  };

  const handleSubmit = async () => {
    if (!feedbackKey) {
      console.error("FeedbackComponent: feedbackKey prop is missing!");
      toast({
        title: "Error",
        description: "Cannot submit feedback (missing key).",
        variant: "destructive",
      });
      return;
    }
    
    if (feedback === null || selectedThumb === null) {
      toast({ title: "Info", description: "Please select thumbs up or down first." });
      return;
    }

    // Call the context function to submit
    await submitFeedback(feedbackKey, feedback, context);
    setIsDialogOpen(false);
  };

  const canSubmit =
    feedbackLoading === false &&
    selectedThumb !== null &&
    (internalFeedbackText.trim() ||
      (showRatings && Object.values(internalRatings).some((r) => r > 0)));

  // Determine if we already have feedback
  const hasFeedback = feedback?.feedbackFlag !== null && feedback?.feedbackFlag !== undefined;

  return (
    <div className="w-full flex flex-col space-y-2">
      <Text className="text-sm">{question}</Text>
      <Button 
        variant={hasFeedback ? "default" : "outline"}
        size="sm"
        onClick={() => setIsDialogOpen(true)}
        className={`w-full ${hasFeedback ? "bg-primary/90" : ""}`}
      >
        <MessageSquareText className="h-4 w-4 mr-1.5" />
        {hasFeedback ? "View Feedback" : "Give Feedback"}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[85vw] sm:max-w-[400px] p-3">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">{question}</DialogTitle>
          </DialogHeader>
          
          <div className="py-3 space-y-3">
            {/* Thumbs */}
            <div className="flex justify-center gap-3">
              <Button
                variant={selectedThumb === true ? "default" : "outline"}
                size="icon"
                className={`h-10 w-10 ${selectedThumb === true ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
                onClick={() => handleThumbClick(true)}
                aria-label="Helpful"
              >
                <ThumbsUp className="h-5 w-5" />
              </Button>
              <Button
                variant={selectedThumb === false ? "default" : "outline"}
                size="icon"
                className={`h-10 w-10 ${selectedThumb === false ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}
                onClick={() => handleThumbClick(false)}
                aria-label="Not Helpful"
              >
                <ThumbsDown className="h-5 w-5" />
              </Button>
            </div>

            {/* Star ratings */}
            {showRatings && (
              <div className="space-y-2 border-b pb-3">
                {ratingLabels.map((label) => (
                  <StarRating
                    key={label}
                    label={label}
                    rating={internalRatings[label] || 0}
                    setRating={(value) => handleRatingChange(label, value)}
                  />
                ))}
              </div>
            )}

            {/* Text feedback */}
            <div className="space-y-1">
              <Label htmlFor={`mobile-feedback-text-${feedbackKey}`} className="text-sm">
                Comments (optional)
              </Label>
              <Textarea
                id={`mobile-feedback-text-${feedbackKey}`}
                placeholder="Your feedback..."
                value={internalFeedbackText}
                onChange={handleTextChange}
                className="min-h-[60px] text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || feedbackLoading}
              className="w-full"
              size="sm"
            >
              {feedbackLoading ? "Submitting..." : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FeedbackComponent = ({
  question = "Was this helpful?",
  showRatings = false,
  feedback = null,
  setFeedback = () => {},
  ratingLabels = [
    "Accuracy",
    "Helpfulness",
    "Coverage",
    "Efficiency",
    "Overall",
  ],
  feedbackKey, // <-- Add feedbackKey prop to identify the feedback type
  context = {}, // <-- Add context prop (e.g., { scheduleId: '...' })
  isMobile = false, // <-- Add isMobile prop to conditionally render mobile version
}) => {
  // Declare all hooks at the top level, before any conditionals
  const { submitFeedback, feedbackLoading } = useContext(AppContext);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false); // For mobile dialog
  const [internalFeedbackText, setInternalFeedbackText] = useState("");
  const [internalRatings, setInternalRatings] = useState({});

  // Initialize internal state from feedback prop only when it changes or on mount
  // This effect runs for both mobile and desktop versions
  useEffect(() => {
    const newFeedbackText = feedback?.feedbackText || "";
    const newRatings =
      feedback?.ratings ||
      ratingLabels.reduce((acc, label) => ({ ...acc, [label]: 0 }), {});

    setInternalFeedbackText(newFeedbackText);
    setInternalRatings(newRatings);

    // Only apply desktop-specific logic if not mobile
    if (!isMobile) {
      // If feedback is initially provided (e.g., loaded from localStorage),
      // keep the popup open if a thumb was selected but maybe not submitted yet.
      if (
        feedback?.feedbackFlag !== null &&
        feedback?.feedbackFlag !== undefined
      ) {
        // Keep popup open if feedback exists and a thumb is selected
        // Note: This might need adjustment based on desired UX after successful submission
        // setIsPopupOpen(true); // Commented out: let thumb clicks control opening
      } else {
        setIsPopupOpen(false); // Close if no feedback flag is set
      }
    }
  }, [feedback, ratingLabels, isMobile]);

  // If mobile, render mobile version
  if (isMobile) {
    return (
      <MobileFeedbackDialog
        question={question}
        showRatings={showRatings}
        feedback={feedback}
        setFeedback={setFeedback}
        ratingLabels={ratingLabels}
        feedbackKey={feedbackKey}
        context={context}
        submitFeedback={submitFeedback}
        feedbackLoading={feedbackLoading}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
      />
    );
  }

  const handleThumbClick = (isPositive) => {
    const currentFlag = feedback?.feedbackFlag;
    let newFeedbackData;

    if (currentFlag === isPositive) {
      // Clicking the same thumb again - reset feedback locally and close popup
      newFeedbackData = {
        feedbackFlag: null, // Explicitly set flag to null
        feedbackText: "",
        ratings: ratingLabels.reduce(
          (acc, label) => ({ ...acc, [label]: 0 }),
          {},
        ),
      };
      setIsPopupOpen(false); // Close popup
    } else {
      // Clicking a different thumb or the first thumb
      // Reset text/ratings only if clicking the *first* time or switching thumbs
      const resetTextAndRatings =
        currentFlag === null || currentFlag === undefined;
      newFeedbackData = {
        feedbackFlag: isPositive,
        feedbackText: resetTextAndRatings ? "" : internalFeedbackText,
        ratings: resetTextAndRatings
          ? ratingLabels.reduce((acc, label) => ({ ...acc, [label]: 0 }), {})
          : internalRatings,
      };
      setIsPopupOpen(true); // Open popup
    }
    setFeedback(newFeedbackData); // Update parent state (which triggers useEffect)
  };

  const handleRatingChange = (label, value) => {
    const updatedRatings = { ...internalRatings, [label]: value };
    setInternalRatings(updatedRatings); // Update internal state first

    // Update parent state *only* with the change (don't overwrite flag/text here)
    const newFeedbackData = {
      ...(feedback || {}), // Preserve existing flag and text
      ratings: updatedRatings,
    };
    setFeedback(newFeedbackData);
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setInternalFeedbackText(newText); // Update internal state first

    // Update parent state *only* with the change (don't overwrite flag/ratings here)
    const newFeedbackData = {
      ...(feedback || {}), // Preserve existing flag and ratings
      feedbackText: newText,
    };
    setFeedback(newFeedbackData);
  };

  const handleSubmit = async () => {
    if (!feedbackKey) {
      console.error("FeedbackComponent: feedbackKey prop is missing!");
      toast({
        title: "Error",
        description: "Cannot submit feedback (missing key).",
        variant: "destructive",
      });
      return;
    }
    if (feedback === null) {
      console.warn("handleSubmit called with null feedback state.");
      toast({ title: "Info", description: "No feedback selected to submit." });
      setIsPopupOpen(false);
      return;
    }

    // Call the context function to submit
    await submitFeedback(feedbackKey, feedback, context);

    // Check for errors via feedbackError state (not directly from submitFeedback)
    // Assuming submitFeedback updates feedbackError state in context
    // Let AppContext handle the success toast

    setIsPopupOpen(false); // Close popup after attempting submit

    // Decide whether to reset local state after submit.
    // If you want the feedback to persist visually until explicitly reset:
    // Do nothing here.
    // If you want to clear the form after successful submit:
    // Check if feedbackLoading is false and feedbackError is null after await
    // If success:
    //   setFeedback({ feedbackFlag: null, feedbackText: "", ratings: {} }); // Reset parent
  };

  const canSubmit =
    feedbackLoading === false && // Don't allow submit while loading
    (internalFeedbackText.trim() ||
      (showRatings && Object.values(internalRatings).some((r) => r > 0)));

  return (
    <div className="flex items-center justify-between gap-2">
      <Text className="text-sm flex-shrink-0 mr-2">{question}</Text>
      <Popover open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <PopoverTrigger asChild>
          <div className="flex gap-1">
            <Button
              variant={feedback?.feedbackFlag === true ? "default" : "outline"}
              size="icon"
              className={`h-8 w-8 ${feedback?.feedbackFlag === true ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
              onClick={() => handleThumbClick(true)}
              aria-label="Helpful"
            >
              <ThumbsUp className="h-4 w-4" />
            </Button>
            <Button
              variant={feedback?.feedbackFlag === false ? "default" : "outline"}
              size="icon"
              className={`h-8 w-8 ${feedback?.feedbackFlag === false ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}
              onClick={() => handleThumbClick(false)}
              aria-label="Not Helpful"
            >
              <ThumbsDown className="h-4 w-4" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="end">
          <div className="space-y-4">
            <h4 className="font-medium leading-none text-sm">
              {feedback?.feedbackFlag === true
                ? "Great! Tell us more."
                : "How can we improve?"}
            </h4>

            {showRatings && (
              <div className="space-y-2 border-b pb-3">
                {ratingLabels.map((label) => (
                  <StarRating
                    key={label}
                    label={label}
                    rating={internalRatings[label] || 0}
                    setRating={(value) => handleRatingChange(label, value)}
                  />
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label
                htmlFor={`feedback-text-${feedbackKey}`}
                className="text-xs"
              >
                Feedback (optional)
              </Label>
              <Textarea
                id={`feedback-text-${feedbackKey}`}
                placeholder="Your comments..."
                value={internalFeedbackText}
                onChange={handleTextChange}
                className="min-h-[60px]"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                size="sm"
                disabled={!canSubmit || feedbackLoading} // Disable button if nothing to submit or during loading
              >
                {feedbackLoading ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default FeedbackComponent;
